import { Injectable, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType, InteractionStatus } from '@azure/msal-browser';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs';
import {
  AuthenticatedIdentity,
  AuthenticationService,
  LogoutOption,
  identityKey,
} from '@ups/security';
import { environment } from '@ups/xplat/core';

/**
 * MSAL implementation of {@link AuthenticationService}. Emits the signed-in
 * account's UPN as an `email` identity so `SecurityService` can look
 * permissions up via the by-email path on the software-security API
 * (Ryn's Roost has no Auth0 sub claim to hand in).
 *
 * Registered in `app.config.ts` against the `SECURITY_IDENTITY_SOURCE`
 * injection token. When absent, `SecurityService` falls back to Auth0's
 * idTokenClaims$ — unchanged behaviour for every other UPS app.
 */
@Injectable({ providedIn: 'root' })
export class MsalSecurityIdentitySource implements AuthenticationService {
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);

  public readonly isAuthenticated$ = new ReplaySubject<boolean>();

  private readonly current$ = new BehaviorSubject<AuthenticatedIdentity | null>(
    null
  );
  readonly identity$: Observable<AuthenticatedIdentity | null> = this.current$
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => identityKey(a) === identityKey(b)));

  readonly claims$ = new BehaviorSubject<Record<string, unknown> | null>(null);

  constructor() {
    // Publish whatever account is already known (page refresh with cached
    // session), then keep in sync with login/logout/account-added events.
    this.current$.next(this.identityFromMsal());

    this.broadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        map(() => this.identityFromMsal())
      )
      .subscribe((identity) => this.current$.next(identity));

    this.broadcast.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
            msg.eventType === EventType.LOGOUT_SUCCESS
        )
      )
      .subscribe(() => {
        this.current$.next(this.identityFromMsal());
        this.claims$.next(
          this.msal.instance.getActiveAccount()?.idTokenClaims ?? null
        );
      });

    this.msal
      .initialize()
      .pipe(map(() => Boolean(this.msal.instance.getActiveAccount())))
      .subscribe(this.isAuthenticated$);
  }

  logout(options: LogoutOption): void {
    this.msal
      .logoutRedirect({ postLogoutRedirectUri: options.returnTo })
      .subscribe();
  }

  getAccessTokenSilently(): Observable<string> {
    return this.msal
      .acquireTokenSilent({
        scopes: ['user.read', `${environment.entraId?.audience}/Access.All`],
      })
      .pipe(map((x) => x.accessToken));
  }

  private identityFromMsal(): AuthenticatedIdentity | null {
    const accounts: AccountInfo[] = this.msal.instance.getAllAccounts();
    const account = accounts[0];
    if (!account) return null;
    // MSAL surfaces the UPN on `username` for Azure AD sign-ins. Fall back
    // to idTokenClaims.preferred_username for B2C/guest edge cases.
    const upn =
      account.username ||
      (account.idTokenClaims as { preferred_username?: string } | undefined)
        ?.preferred_username;
    if (!upn) return null;
    return { kind: 'email', email: upn };
  }
}
